import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ShieldCheck, Loader2, Building2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/api";
import { PhoneInput } from "@/components/PhoneInput";
import { OtpStep } from "@/components/OtpStep";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const DEVICE_KEY = "vlog_parent_device";

export default function ParentLogin() {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const nav = useNavigate();

  const finish = (data) => {
    localStorage.setItem("vlog_parent_token", data.token);
    if (data.device_token) localStorage.setItem(DEVICE_KEY, data.device_token);
    toast.success("Welcome back");
    nav("/parent");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/parent/login`, { phone, password, device_token: localStorage.getItem(DEVICE_KEY) || "" });
      if (data.otp_required) setChallenge({ challengeId: data.challenge_id, emailHint: data.email_hint, emailed: data.emailed });
      else finish(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Sign in failed");
    } finally {
      setBusy(false);
    }
  };

  const verifyOtp = async (challengeId, code, remember) => {
    try {
      const { data } = await axios.post(`${API}/parent/otp/verify`, { challenge_id: challengeId, code, remember_device: remember });
      return { ok: true, ...data };
    } catch (err) {
      return { ok: false, error: formatApiErrorDetail(err.response?.data?.detail) || "Could not verify the code" };
    }
  };

  const resendOtp = async (challengeId) => {
    try {
      const { data } = await axios.post(`${API}/parent/otp/resend`, { challenge_id: challengeId });
      return { ok: true, challengeId: data.challenge_id, emailed: data.emailed };
    } catch (err) {
      return { ok: false, error: formatApiErrorDetail(err.response?.data?.detail) || "Could not resend the code" };
    }
  };

  return (
    <div className="min-h-screen bg-navy-hero flex flex-col" data-testid="parent-login-page">
      <div className="px-8 py-6 flex items-center justify-between gap-4">
        <div className="inline-flex items-center gap-2 text-slate-700" data-testid="parent-login-secured-pill">
          <ShieldCheck className="h-4 w-4 text-primary" />
          <span className="text-xs uppercase tracking-widest">Secured access · sign-in required</span>
        </div>
        <Link to="/login" data-testid="parent-login-staff-btn" className="inline-flex items-center gap-2 rounded-full border border-sky-300/60 bg-white/80 backdrop-blur px-4 py-2 text-sm font-semibold text-primary shadow-sm hover:bg-primary hover:text-white transition">
          <Building2 className="h-4 w-4" /> Staff login
        </Link>
      </div>
      <div className="flex flex-1 items-center justify-center px-6">
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-10 shadow-xl shadow-sky-900/15">
          <div className="flex items-center gap-3 mb-8">
            <img src="/logerd-logo.png" alt="Logerd" className="h-14 w-14 object-contain flex-shrink-0" />
            <div>
              <div className="font-display text-2xl font-bold tracking-tight text-slate-900">Logerd</div>
              <div className="text-sm text-slate-500">{challenge ? "Two-step verification" : "Parent sign-in"}</div>
            </div>
          </div>

          {challenge ? (
            <OtpStep challenge={challenge} verify={verifyOtp} resend={resendOtp}
                     onBack={() => setChallenge(null)} onDone={finish} />
          ) : (
          <form onSubmit={submit} className="space-y-4" data-testid="parent-login-form">
            <label className="block">
              <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Mobile number</span>
              <PhoneInput
                required
                value={phone}
                onChange={setPhone}
                data-testid="parent-login-phone"
                placeholder="(214) 555-0100"
                className="w-full h-12 rounded-lg border border-slate-200 bg-white px-4 text-base font-body focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
            </label>
            <label className="block">
              <span className="block mb-1.5 text-xs font-medium uppercase tracking-widest text-slate-500">Password</span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="parent-login-password"
                className="w-full h-12 rounded-lg border border-slate-200 bg-white px-4 text-base font-body focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
            </label>
            <button
              type="submit"
              disabled={busy}
              data-testid="parent-login-submit"
              className="w-full h-12 rounded-lg bg-primary text-white font-display text-base font-semibold tracking-tight transition active:scale-[0.98] hover:shadow disabled:opacity-60 inline-flex items-center justify-center gap-2"
            >
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign in
            </button>
          </form>
          )}

          <div className="mt-6 text-xs text-slate-500 border-t border-slate-100 pt-4">
            No account yet? Ask your school to send you an invite link — you'll message your child's teacher and get school updates.
          </div>
        </div>
      </div>
    </div>
  );
}
